/* eslint-disable no-console */
import { useMemo, useCallback } from "react";
import {
  detalleGestionContenidosCombinado,
  ingresosGestionContenidosMensualAnoAnteriorCombinado,
  ingresosGestionContenidosMensualChileCombinado,
  ingresosGestionContenidosMensualCombinado,
  ingresosGestionContenidosMensualPeruCombinado,
} from "../../../utils/carteleria/ingresosGestionContenidosCarteleria";
import { calcularCostoSoftwareCarteleria } from "../estadoResultadosER";

export function useGestionCalcs({
  pais,
  selectedYear,
  monthsToShow,
  currentYear,
  modoCombinado,
  negociosCarteleria,
  negociosCarteleriaChile,
  negociosCarteleriaPeru,
  clientes,
  edificios,
  costosGestionData,
  costosPublicidadData,
  convertirAMonedaLocal,
  getCostoGestionValue,
  getDolarValue,
  dolarMensualPeru,
  dolarMensualChile,
  dolarMensualPromedio,
  dolarMensualPromedioAnoAnterior,
  dolarFallback,
  modalRefresh,
}) {
  // F3d: los ingresos de gestión y contenidos (1, 5-8) = histórico congelado (< 2026-10, `utils/carteleria/historico/
  // useGestionCalcsIngresosHistorico.js`) + motor (>= 2026-10), combinados en `utils/carteleria/
  // ingresosGestionContenidosCarteleria.js`. `getCuotasFacturadasPorMes` sigue viviendo en `estadoResultadosER.js`.

  // ─── 1. detalleGestionContenidosPorMes ───
  // F3d: meses < 2026-10 → lógica histórica congelada; >= 2026-10 → motor (ver ingresosGestionContenidosCarteleria.js).
  const detalleGestionContenidosPorMes = useMemo(
    () =>
      detalleGestionContenidosCombinado(
        { negocios: negociosCarteleria, clientes, edificios, selectedYear, pais, convertirAMonedaLocal },
        new Date()
      ),
    [
      negociosCarteleria,
      clientes,
      edificios,
      selectedYear,
      currentYear,
      pais,
      convertirAMonedaLocal,
      costosGestionData,
      modalRefresh,
    ]
  );

  // ─── 2. calcularCostoSoftwareEstimado ───
  // PASO 3 (Criterio 1), punto 3: delega en la función ÚNICA `calcularCostoSoftwareCarteleria`
  // (`estadoResultadosER.js`) — antes esta fórmula vivía inline acá Y, divergente, en
  // `calcularCostoCarteleriaEstimado` (camino combinado/región) — ver el comentario largo junto
  // a esa función para la medición/causa de la unificación. `pantallasActivasPorMes`/
  // `calcularPantallasActivasPorMes` locales se retiraron: la función única ya cuenta pantallas
  // por dentro con el mismo mecanismo (ahora con el tope de `plazo_contrato_meses` corregido).
  //
  // AJUSTE de Cowork (2026-10-06): el TC de este estimado (sin fecha propia) es el PROMEDIO
  // real del mes (Criterio 1b), no el día 1 — `dolarMensualPromedio` (prop, de
  // `useExchangeRates`) en vez de `getDolarValue(fechaDia1)`. Recibido como prop YA gateado
  // por `loaded` (no se recalcula acá con un `useMemo` propio sin ese gate — bug real
  // encontrado en este punto: sin `loaded` en las deps, el array quedaba congelado en el
  // valor del PRIMER render, de antes de que la cache de TC cargara async).
  const calcularCostoSoftwareEstimado = useCallback(
    (monthIndex) => calcularCostoSoftwareCarteleria(negociosCarteleria, selectedYear, monthIndex, dolarMensualPromedio),
    [negociosCarteleria, selectedYear, dolarMensualPromedio]
  );

  // Gemelo exacto de arriba, un año atrás — lo necesita `calcularCostosVariablesAnoAnterior`
  // (`estadoResultadosER.js`, PASO 3.7) para no restarle al Software DS real del año anterior
  // el estimado de pantallas del año EN CURSO (bug aislado en la Fase 1b). FIX (punto 3 del
  // encargo, hallazgo 4): antes la fecha del TC armaba con `currentYear` (el año de HOY) en vez
  // de `selectedYear - 1` (el año cuyas pantallas cuenta) — un bug real, aislado, corregido acá
  // porque la función única recibe `ano` explícito y lo usa para las dos cosas.
  const calcularCostoSoftwareEstimadoAnoAnterior = useCallback(
    (monthIndex) =>
      calcularCostoSoftwareCarteleria(
        negociosCarteleria,
        selectedYear - 1,
        monthIndex,
        dolarMensualPromedioAnoAnterior
      ),
    [negociosCarteleria, selectedYear, dolarMensualPromedioAnoAnterior]
  );

  // ─── 3. getFechaFacturaHardware ───
  const getFechaFacturaHardware = useCallback(
    (negocio, mesIndex) => {
      const registro = costosGestionData.find(
        (c) =>
          Number(c.negocio_id) === Number(negocio.id) &&
          Number(c.mes) === mesIndex + 1 &&
          Number(c.ano) === currentYear &&
          c.fecha_factura
      );
      if (registro?.fecha_factura) return registro.fecha_factura.split("T")[0];
      const ultimoDia = new Date(currentYear, mesIndex + 1, 0);
      return ultimoDia.toISOString().split("T")[0];
    },
    [costosGestionData, currentYear]
  );

  // ─── 4. getSoftwareDSValueA ───
  const getSoftwareDSValueA = useCallback(
    (monthIndex) => {
      const row = costosPublicidadData[monthIndex];
      const valorTotalFactura = row ? Number(row.software_ds) || 0 : 0;
      const costoCarteleria = calcularCostoSoftwareEstimado(monthIndex);
      return Math.max(0, valorTotalFactura - costoCarteleria);
    },
    [costosPublicidadData, calcularCostoSoftwareEstimado]
  );

  // ─── 5. ingresosGestionContenidosMensual (current country) ───
  const ingresosGestionContenidosMensual = useMemo(
    () =>
      ingresosGestionContenidosMensualCombinado(
        {
          negocios: negociosCarteleria,
          selectedYear,
          currentYear,
          pais,
          convertirAMonedaLocal,
          costosGestionData,
        },
        new Date()
      ),
    [negociosCarteleria, selectedYear, currentYear, pais, convertirAMonedaLocal, costosGestionData]
  );

  // ─── 6. ingresosGestionContenidosMensualAnoAnterior ───
  const añoAnteriorGestion = selectedYear - 1;
  const ingresosGestionContenidosMensualAnoAnterior = useMemo(
    () =>
      ingresosGestionContenidosMensualAnoAnteriorCombinado(
        { negocios: negociosCarteleria, añoAnteriorGestion, pais, convertirAMonedaLocal },
        new Date()
      ),
    [negociosCarteleria, añoAnteriorGestion, pais, convertirAMonedaLocal]
  );

  // ─── 7. ingresosGestionContenidosMensualChile ───
  const ingresosGestionContenidosMensualChile = useMemo(
    () =>
      ingresosGestionContenidosMensualChileCombinado(
        { negocios: negociosCarteleriaChile, selectedYear, convertirAMonedaLocal },
        new Date()
      ),
    [negociosCarteleriaChile, selectedYear, convertirAMonedaLocal]
  );

  // ─── 8. ingresosGestionContenidosMensualPeru ───
  // Conversión a PEN con el TC MENSUAL de Perú (`dolarMensualPeru[mes]`), igual que el `toPEN` anterior.
  const ingresosGestionContenidosMensualPeru = useMemo(
    () =>
      ingresosGestionContenidosMensualPeruCombinado(
        { negocios: negociosCarteleriaPeru, selectedYear, dolarMensualPeru, dolarFallback },
        new Date()
      ),
    [negociosCarteleriaPeru, selectedYear, dolarMensualPeru, dolarFallback]
  );

  // ─── 9. ytdSoftwareDS, ytdOtros ───
  const ytdSoftwareDS = useMemo(() => {
    let total = 0;
    for (let i = 0; i < monthsToShow; i++) {
      const valor = getCostoGestionValue(i, "software_ds") || 0;
      total += valor > 0 ? valor : calcularCostoSoftwareEstimado(i);
    }
    return total;
  }, [monthsToShow, calcularCostoSoftwareEstimado, costosGestionData]);

  const ytdOtros = costosGestionData.slice(0, monthsToShow).reduce((a, row) => a + (Number(row?.otros) || 0), 0);

  // ─── 10. margenGestionContenidosMensual ───
  const margenGestionContenidosMensual = useMemo(
    () =>
      Array(12)
        .fill(0)
        .map((_, i) => {
          if (i >= monthsToShow) return 0;
          const ingresos = ingresosGestionContenidosMensual[i] || 0;
          const softwareSupabase = Number(getCostoGestionValue(i, "software_ds")) || 0;
          const softwareEstimado = calcularCostoSoftwareEstimado(i);
          const softwareUsado = softwareSupabase > 0 ? softwareSupabase : softwareEstimado;
          const otros = Number(getCostoGestionValue(i, "otros")) || 0;
          return ingresos - softwareUsado - otros;
        }),
    [ingresosGestionContenidosMensual, costosGestionData, monthsToShow, calcularCostoSoftwareEstimado]
  );

  // ─── 11. YTDs ───
  const ytdMargenGestionContenidos = useMemo(
    () => margenGestionContenidosMensual.slice(0, monthsToShow).reduce((sum, val) => sum + val, 0),
    [margenGestionContenidosMensual, monthsToShow]
  );

  const ytdIngresosGestionContenidos = useMemo(
    () => ingresosGestionContenidosMensual.slice(0, monthsToShow).reduce((acc, val) => acc + val, 0),
    [ingresosGestionContenidosMensual, monthsToShow]
  );

  // ─── 12. gestionMensualesCombinados ───
  const gestionMensualesCombinados = useMemo(() => {
    if (!modoCombinado) return null;
    const resultado = Array(12).fill(0);
    for (let i = 0; i < 12; i++) {
      const gestionChile = ingresosGestionContenidosMensualChile[i] || 0;
      const gestionPeru = ingresosGestionContenidosMensualPeru[i] || 0;
      const chileUSD = gestionChile / (dolarMensualChile[i] || dolarFallback.Chile);
      const peruUSD = gestionPeru / (dolarMensualPeru[i] || dolarFallback["Perú"]);
      resultado[i] = chileUSD + peruUSD;
    }
    return resultado;
  }, [
    modoCombinado,
    ingresosGestionContenidosMensualChile,
    ingresosGestionContenidosMensualPeru,
    dolarMensualChile,
    dolarMensualPeru,
  ]);

  return {
    detalleGestionContenidosPorMes,
    calcularCostoSoftwareEstimado,
    calcularCostoSoftwareEstimadoAnoAnterior,
    getSoftwareDSValueA,
    getFechaFacturaHardware,
    ingresosGestionContenidosMensual,
    ingresosGestionContenidosMensualAnoAnterior,
    ingresosGestionContenidosMensualChile,
    ingresosGestionContenidosMensualPeru,
    gestionMensualesCombinados,
    ytdSoftwareDS,
    ytdOtros,
    margenGestionContenidosMensual,
    ytdMargenGestionContenidos,
    ytdIngresosGestionContenidos,
  };
}
